import React from 'react';

import { REPORTS_TABLES_COLUMNS } from './constants/ReportsTablesColumns';

import { ReportChart } from './components/Charts';
import { ChartContainer, Column, Columns, PageContent, PageHeader, Section } from './components/Layout';
import { ClassificationsReportTable, LocationsReportTable, LotsReportTable } from './components/Tables';

import { PageTitle, SectionTitle } from './components/Titles';
import { SpinnerLoader } from './components/Loaders';
import { ReportTableType } from './types/ReportTableType';
import {
    FiltersDropdown,
    ReportTypeSelectionDropdown,
    SelectionDropdown,
    WidgetsDropdown,
} from './components/Dropdowns';
import { ChartTypeToggleButton } from './components/Buttons';

import { ChartType } from 'chart.js';

import { Classification } from './types/ClassificationType';

import { renderReportSectionTitle } from './helpers/renderReportSectionTitle';
import { retrieveMainsLotsLocations } from './helpers/retrieveMainsLotsLocations';
import { getLibraryIdFromUrl } from './helpers/getLibraryIdFromUrl';
import { flattenClassificationsData } from './helpers/flattenClassificationsData';
import { applyChildAncestorsProp } from './helpers/applyChildAncestorsProp';
import { transformHierarchyWithLevel } from './helpers/transformHierarchyWithLevel';
import { applyFiltersOnClassificationsReport } from './helpers/applyFiltersOnClassificationsReport';

import useHttp from './hooks/useHttp.hook';
import useWidgets from './hooks/useWidgets.hook';
import useCharts from './hooks/useCharts.hook';
import useFilters from './hooks/useFilters.hook';

declare global {
    interface Window {
        env: Record<string, string>;
    }
}

const selectionReportTypes: ReportTableType[] = Object.keys(REPORTS_TABLES_COLUMNS).map(
    (key) => key as ReportTableType
);

const Root = (props) => {
    const currentUser = localStorage.getItem('currentUser');
    const currentAccessToken = localStorage.getItem('accessToken');

    const urlPathName = window.location.pathname;

    const [isLoading, setIsLoading] = React.useState<boolean>(false);

    const { sendRequest, error } = useHttp();
    const { selectedWidgets, handleSelectWidget } = useWidgets();

    const isChartWidgetDisplayed = React.useMemo(() => {
        return selectedWidgets?.find((widget: Record<string, any>) => widget.key === 'report_chart').isSelected;
    }, [selectedWidgets]);

    const [selectedReportType, setSelectedReportType] = React.useState<ReportTableType>('nodes-report');
    const handleSelectReportType = (selectedItem: ReportTableType) => {
        setSelectedReportType(selectedItem as ReportTableType);
    };

    const [selectedChartType, setSelectedChartType] = React.useState<ChartType>('pie');
    const handleSelectChartType = (selectedItem: ChartType) => {
        setSelectedChartType(selectedItem);
    };
    const [selectedClassificationType, setSelectedClassificationType] =
        React.useState<Classification>('UNIFORMATII2015');
    const handleSelectClassificationType = (selectedItem: Classification) => {
        setSelectedClassificationType(selectedItem);
    };

    const { selectedReportFilters, handleSelectFilters } = useFilters(selectedReportType);

    // Table Montant H.T par Lot
    const [lotsTotalData, setLotsTotalData] = React.useState<Record<string, any>[]>([]);

    // Table Montant H.T par Lot et par Localisation
    const [lotsTotalByLocationData, setLotsTotalByLocationData] = React.useState<Record<string, any>[]>([]);

    // Table Montant H.T par Classification
    const [reportClassificationData, setReportClassificationData] = React.useState<Record<string, any>[]>([]);

    React.useEffect(() => {
        let libraryId;

        if (urlPathName) {
            libraryId = getLibraryIdFromUrl(urlPathName);
        }

        const fetchLotsTotal = async () => {
            setIsLoading(true);
            const url = new URL(window.env.LIBRARY_API_URL + 'api/library/' + libraryId + '/get-lots-totals');
            try {
                const responseData = await sendRequest(url, 'GET', null, {
                    Authorization: `Bearer ${currentAccessToken}`,
                    'Content-Type': 'application/json',
                });

                const tmpLotsTotalData = responseData.map((lot) => {
                    return {
                        lotId: lot.lotId,
                        lotCode: lot.lotCode,
                        lotTitle: lot.lotTitle,
                        lotTotalExclVat: lot.totalExcl,
                    };
                });

                setLotsTotalData(tmpLotsTotalData);
                setIsLoading(false);
            } catch (err) {
                console.log(err);
                setIsLoading(false);
            }
        };

        const fetchLotsTotalByLocation = async () => {
            setIsLoading(true);
            const url = new URL(
                window.env.LIBRARY_API_URL + 'api/library/' + libraryId + '/get-lots-totals/by-location'
            );
            try {
                const responseData = await sendRequest(url, 'GET', null, {
                    Authorization: `Bearer ${currentAccessToken}`,
                    'Content-Type': 'application/json',
                });

                setLotsTotalByLocationData(responseData);
                setIsLoading(false);
            } catch (err) {
                console.log(err);
                setIsLoading(false);
            }
        };

        const fetchClassificationsTotals = async (format: string) => {
            setIsLoading(true);

            // const classificationsLibUrl = new URL('http://localhost:3000/api/classifications/' + format);
            const classificationsLibUrl = new URL(window.env.CLASSIFICATIONS_API_URL + 'api/classifications/' + format);
            const classificationsTotalsUrl = new URL(
                window.env.LIBRARY_API_URL + 'api/library/' + libraryId + '/get-classification-totals'
            );

            try {
                // 1. Fetch classifications library
                const classificationsLibResponse = await fetch(classificationsLibUrl, {
                    method: 'GET',
                    body: null,
                    headers: {},
                });

                if (!classificationsLibResponse.ok) {
                    throw new Error('Error occurred while fetching classifications library');
                }

                const classificationsLibData = await classificationsLibResponse.json();

                // 2. Fetch classifications totals
                const classificationsTotalResponse = await fetch(classificationsTotalsUrl, {
                    method: 'GET',
                    body: null,
                    headers: {
                        Authorization: `Bearer ${currentAccessToken}`,
                        'Content-Type': 'application/json',
                    },
                });

                if (!classificationsTotalResponse.ok) {
                    throw new Error('Error occurred while fetching classifications totals');
                }

                const classificationsTotalData = await classificationsTotalResponse.json();

                // 3. Rework and merge data
                const selectedClassificationTotalData: Record<string, any> = classificationsTotalData.find(
                    (classification) => classification.classificationEdition === selectedClassificationType
                );

                const selectedClassificationLibData = classificationsLibData.data;
                const selectedClassificationWithParentId = applyChildAncestorsProp(selectedClassificationLibData);

                const flattedClassificationsLibData = flattenClassificationsData(selectedClassificationWithParentId);

                const updatedReportClassificationData = [];
                flattedClassificationsLibData.forEach((libClassification) => {
                    const foundClassificationTotal = selectedClassificationTotalData.totals.find(
                        (classification) => classification.classificationCode === libClassification.code
                    );

                    if (!foundClassificationTotal) {
                        updatedReportClassificationData.push({
                            classificationCode: libClassification.code,
                            classificationTitle: libClassification.label,
                            classificationUnit: libClassification.unit,
                            classificationLevel: libClassification.level,
                            classificationAncestors: libClassification.ancestors,
                            totalExclVat: 0,
                        });
                    }

                    if (foundClassificationTotal) {
                        updatedReportClassificationData.push({
                            classificationCode: libClassification.code,
                            classificationTitle: libClassification.label,
                            classificationUnit: libClassification.unit,
                            classificationLevel: libClassification.level,
                            classificationAncestors: libClassification.ancestors,
                            totalExclVat: foundClassificationTotal.totalExclVat,
                        });
                    }
                });

                setReportClassificationData(updatedReportClassificationData);
            } catch (err) {
                console.log(err);
            } finally {
                setIsLoading(false);
            }
        };

        if (selectedReportType === 'nodes-report') {
            fetchLotsTotal();
        }

        if (selectedReportType === 'nodes-by-location-report') {
            fetchLotsTotalByLocation();
        }

        if (selectedReportType === 'classification-report') {
            fetchClassificationsTotals(selectedClassificationType.toLowerCase());
        }
    }, [selectedReportType, selectedClassificationType, urlPathName]);

    const SelectedTableReport: React.FC = React.useCallback((): JSX.Element | null => {
        if (selectedReportType === 'nodes-report') {
            return <LotsReportTable reportType={selectedReportType} bodyTableData={lotsTotalData} />;
        }

        if (selectedReportType === 'nodes-by-location-report') {
            if (lotsTotalByLocationData && lotsTotalByLocationData.length > 0) {
                const mainsLocations = retrieveMainsLotsLocations(lotsTotalByLocationData, true);

                // retrive main locations > display in header
                const locationsColumnsHeader = mainsLocations.map((location) => {
                    return {
                        id: location.locationId,
                        key: location.locationTitle.replace(' ', '_').toLowerCase(),
                        title: location.locationTitle,
                        minWidth: 0,
                        maxWidth: 0,
                        additionalClasses: 'has-text-right',
                    };
                });

                // retrieve and rework data > display in body
                const localizedLotsTableBody = [];

                lotsTotalByLocationData.forEach((lot) => {
                    if (lot.locations && lot.locations.length > 0) {
                        const currentLotLocations = [...lot.locations];
                        const locationsRef = locationsColumnsHeader.map((headerLocation) => {
                            return {
                                locationId: headerLocation.id,
                                locationTitle: headerLocation.title,
                                totalExclVat: null,
                            };
                        });

                        const updatedLotLocations = locationsRef.map((locationRef) => {
                            const foundCurrentLocation = currentLotLocations.find(
                                (currentLoc) => currentLoc.locationId === locationRef.locationId
                            );
                            const isNotLocalized = locationRef.locationId === 'NOT_LOCALIZED';

                            if (!foundCurrentLocation) {
                                if (!isNotLocalized) {
                                    return {
                                        ...locationRef,
                                        totalExclVat: 0,
                                    };
                                }

                                return {
                                    ...locationRef,
                                    totalExclVat: lot.lotNoLocationsTotalExclVat,
                                };
                            }

                            if (foundCurrentLocation && foundCurrentLocation.locationId === 'NOT_LOCALIZED') {
                                return {
                                    ...locationRef,
                                    totalExclVat: lot.lotNoLocationsTotalExclVat,
                                };
                            }

                            return {
                                ...locationRef,
                                totalExclVat: foundCurrentLocation.totalExclVat,
                            };
                        });

                        localizedLotsTableBody.push({
                            ...lot,
                            locations: updatedLotLocations,
                            locationsTotalExclVat: lot.lotLocationsTotalExclVat,
                            lotTotalExclVat: lot.lotLocationsTotalExclVat + lot.lotNoLocationsTotalExclVat,
                        });
                    }
                });

                // compute lots location total for each location > display in footer
                const lotsLocationColumnsFooter = [];
                mainsLocations.forEach((loc) => {
                    let locationColumn = {
                        locationId: loc.locationId,
                        locationTitle: loc.locationTitle,
                        lotsLocTotalExclVat: null,
                    };

                    let sumLotsLocTotalExclVat = 0;
                    localizedLotsTableBody.forEach((lot) => {
                        const foundLocation = lot.locations.find((loc) => loc.locationId === locationColumn.locationId);
                        sumLotsLocTotalExclVat += foundLocation.totalExclVat;
                    });

                    locationColumn = {
                        ...locationColumn,
                        lotsLocTotalExclVat: sumLotsLocTotalExclVat,
                    };

                    lotsLocationColumnsFooter.push(locationColumn);
                });

                return (
                    <LocationsReportTable
                        reportType={selectedReportType}
                        headerTableData={locationsColumnsHeader}
                        bodyTableData={localizedLotsTableBody}
                        footerTableData={lotsLocationColumnsFooter}
                    />
                );
            }

            return null;
        }

        if (selectedReportType === 'classification-report') {
            // !!! TMP to be changed with filter level state
            const tmpLevelBaseDisplayed = 1;
            // !!! TMP
            const reworkReportClassificationData = transformHierarchyWithLevel(
                reportClassificationData,
                tmpLevelBaseDisplayed
            );

            const filteredReportClassificationData = applyFiltersOnClassificationsReport(
                reworkReportClassificationData,
                selectedReportFilters
            );

            return (
                <React.Fragment>
                    <SelectionDropdown
                        selectedItem={selectedClassificationType}
                        selectionItems={['UNIFORMATII2015', 'FB5UNIFORMAT']}
                        onSelectItem={handleSelectClassificationType}
                        additionalClasses="mb-4 mr-4"
                    />

                    <ClassificationsReportTable
                        reportType={selectedReportType}
                        bodyTableData={filteredReportClassificationData}
                    />
                </React.Fragment>
            );
        }

        if (selectedReportType === 'nodes-by-category-and-location-report') {
            return null;
        }
    }, [
        selectedReportType,
        selectedClassificationType,
        lotsTotalData,
        lotsTotalByLocationData,
        reportClassificationData,
        selectedReportFilters,
    ]);

    const SelectedChartReport: React.FC = React.useCallback((): JSX.Element => {
        let chartData = {
            labels: [],
            itemsData: [],
        };

        if (selectedReportType === 'nodes-report') {
            const labels = lotsTotalData.filter((lot) => lot.lotTotalExclVat > 0).map((lot) => lot.lotTitle);
            const itemsData = lotsTotalData.filter((lot) => lot.lotTotalExclVat > 0).map((lot) => lot.lotTotalExclVat);
            chartData = {
                labels: labels,
                itemsData: itemsData,
            };
        }

        if (selectedReportType === 'nodes-by-location-report') {
            if (lotsTotalByLocationData && lotsTotalByLocationData.length > 0) {
                const mainsLocations = retrieveMainsLotsLocations(lotsTotalByLocationData, false);

                const labels = mainsLocations.map((location) => location.locationTitle);
                const itemsData = mainsLocations.map((location) => {
                    const totalExclVat = lotsTotalByLocationData.reduce((acc, lot) => {
                        const locationTotal = lot.locations.find((loc) => loc.locationId === location.locationId);
                        return acc + (locationTotal ? locationTotal.totalExclVat : 0);
                    }, 0);
                    return totalExclVat;
                });
                chartData = {
                    labels: labels,
                    itemsData: itemsData,
                };
            }
        }

        if (selectedReportType === 'classification-report') {
            const labels = reportClassificationData
                .filter((classification) => classification.totalExclVat > 0)
                .map((classification) => classification.classificationCode);
            const itemsData = reportClassificationData
                .filter((classification) => classification.totalExclVat > 0)
                .map((classification) => classification.totalExclVat);
            chartData = {
                labels: labels,
                itemsData: itemsData,
            };
        }

        return <ReportChart chartType={selectedChartType} data={chartData} />;
    }, [selectedReportType, selectedChartType, lotsTotalData, lotsTotalByLocationData, reportClassificationData]);

    return (
        <main className="content--main flex-grow">
            <div className="page page-settings">
                <PageHeader>
                    <PageTitle title="Rapport TCE" />
                </PageHeader>
                <Section additionalClasses="mb-4">
                    <ReportTypeSelectionDropdown
                        selectedItem={selectedReportType}
                        selectionItems={selectionReportTypes}
                        onSelectItem={handleSelectReportType}
                        disabled={isLoading}
                    />

                    {selectedReportFilters.length > 0 && (
                        <FiltersDropdown
                            title={'Filtres'}
                            selectionItems={selectedReportFilters}
                            onSelectItem={handleSelectFilters}
                            disabled={isLoading}
                        />
                    )}

                    <WidgetsDropdown
                        selectionItems={selectedWidgets}
                        onSelectItem={handleSelectWidget}
                        disabled={isLoading}
                    />
                </Section>

                <PageContent>
                    {isLoading && <SpinnerLoader />}
                    {!isLoading && (
                        <React.Fragment>
                            <Columns>
                                {/* TABLE SECTION - START */}
                                <Column>
                                    <Section>
                                        <SelectedTableReport />
                                    </Section>
                                </Column>
                                {/* TABLE SECTION - END */}
                                {/* CHART SECTION - START */}
                                {isChartWidgetDisplayed && (
                                    <Column>
                                        <Section>
                                            <Columns>
                                                <Column>
                                                    <SectionTitle
                                                        title={renderReportSectionTitle(selectedReportType)}
                                                    />
                                                </Column>
                                                <Column additionalClasses="is-flex is-justify-content-end">
                                                    <ChartTypeToggleButton
                                                        selectedChartType={selectedChartType}
                                                        onSelectionHandler={handleSelectChartType}
                                                    />
                                                </Column>
                                            </Columns>

                                            <ChartContainer>
                                                <SelectedChartReport />
                                            </ChartContainer>
                                        </Section>
                                    </Column>
                                )}
                                {/* CHART SECTION - END */}
                            </Columns>
                        </React.Fragment>
                    )}
                </PageContent>
            </div>
        </main>
    );
};

export default Root;
